import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { BookOpenIcon, CheckCircleIcon, PlusCircleIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { initializeContentTrackProgress } from '../utils/contentTrackProgress';

const BrowseContentTracks = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [contentTracks, setContentTracks] = useState([]);
  const [subscribedTrackId, setSubscribedTrackId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [isChild, setIsChild] = useState(false);
  const [discipleFirstName, setDiscipleFirstName] = useState('');

  useEffect(() => {
    loadData();
  }, [discipleId, currentUser]);

  const loadData = async () => {
    if (!currentUser || !discipleId) return;

    try {
      setLoading(true);
      const userAppID = currentUser.appID || currentUser.uid;

      // Load disciple to get name and isChild flag
      const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
      if (discipleDoc.exists()) {
        const discipleData = discipleDoc.data();
        const firstName = discipleData?.name?.split(' ')[0] || '';
        setDiscipleFirstName(firstName);
        setIsChild(discipleData?.isChild === true);
      }

      // Load current subscription
      const mentorDetailsDoc = await getDoc(
        doc(db, 'disciples', discipleId, 'mentorDetails', userAppID)
      );

      if (mentorDetailsDoc.exists()) {
        const data = mentorDetailsDoc.data();
        setSubscribedTrackId(data?.subscribedContentTrackId || null);
      }

      // Load available content tracks (filtered based on isChild)
      await loadContentTracks();
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load content tracks');
    } finally {
      setLoading(false);
    }
  };

  const loadContentTracks = async () => {
    try {
      const publishedQuery = query(
        collection(db, 'contentTracks'),
        where('status', '==', 'published')
      );
      
      const activeQuery = query(
        collection(db, 'contentTracks'),
        where('status', '==', 'active')
      );
      
      const publicQuery = query(
        collection(db, 'contentTracks'),
        where('visibility.isPublic', '==', true)
      );
      
      const [publishedSnap, activeSnap, publicSnap] = await Promise.all([
        getDocs(publishedQuery),
        getDocs(activeQuery),
        getDocs(publicQuery),
      ]);
      
      const allDocs = [...publishedSnap.docs, ...activeSnap.docs, ...publicSnap.docs];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.id === doc.id)
      );

      const allTracksData = uniqueDocs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter tracks based on whether disciple is a child
      const filteredTracks = allTracksData.filter(track => {
        if (isChild) {
          // For child disciples: show only children's tracks
          return track.trackType === 'children';
        } else {
          // For non-child disciples: show only standard tracks (or tracks without trackType for backward compatibility)
          return track.trackType === 'standard' || track.trackType === undefined;
        }
      });

      setContentTracks(filteredTracks);
    } catch (error) {
      console.error('Error loading content tracks:', error);
      setContentTracks([]);
    }
  };

  const handleSubscribe = async (trackId) => {
    if (!currentUser || !discipleId) return;

    try {
      setSubscribing(trackId);

      const userAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);

      // Initialize content track progress
      const progress = await initializeContentTrackProgress(trackId, discipleId, userAppID);

      // Update mentor details
      await setDoc(mentorDetailsRef, {
        subscribedContentTrackId: trackId,
        contentTrackProgress: progress,
      }, { merge: true });

      setSubscribedTrackId(trackId);
      alert('Content track subscribed successfully!');
    } catch (error) {
      console.error('Error subscribing to track:', error);
      alert('Failed to subscribe to content track');
    } finally {
      setSubscribing(null);
    }
  };

  const handleUnsubscribe = async () => {
    if (!currentUser || !discipleId) return;

    if (!window.confirm('Are you sure you want to unsubscribe from this content track?')) {
      return;
    }

    try {
      const userAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);

      await updateDoc(mentorDetailsRef, {
        subscribedContentTrackId: null,
        contentTrackProgress: null,
      });

      setSubscribedTrackId(null);
      alert('Unsubscribed from content track');
    } catch (error) {
      console.error('Error unsubscribing:', error);
      alert('Failed to unsubscribe');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content tracks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Browse Content Tracks</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Instruction */}
        {discipleFirstName && (
          <div className="mb-6 text-center">
            <p className="text-sm text-gray-600 mb-2">
              {isChild 
                ? `Select a children's content track for ${discipleFirstName}`
                : `Select a content track for ${discipleFirstName}`}
            </p>
            {isChild && (
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                Children's Content
              </div>
            )}
          </div>
        )}

        {contentTracks.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {isChild 
                ? "No children's content tracks available"
                : "No content tracks available"}
            </h3>
            <p className="text-gray-600">
              {isChild
                ? "Children's content tracks will appear here once they are created in the system."
                : "Content tracks will appear here once they are created in the system."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contentTracks.map((track) => {
              const isSubscribed = subscribedTrackId === track.id;
              const isSubscribing = subscribing === track.id;

              return (
                <div key={track.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <BookOpenIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900">{track.name}</h3>
                        {track.trackType === 'children' && (
                          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                            </svg>
                            Children's
                          </span>
                        )}
                      </div>
                      {track.trackType === 'children' && track.targetAgeRange && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Ages {track.targetAgeRange}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {track.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{track.description}</p>
                  )}

                  <div className="mt-4">
                    {isSubscribed ? (
                      <button
                        onClick={handleUnsubscribe}
                        disabled={subscribing !== null}
                        className="w-full flex items-center justify-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
                      >
                        <CheckCircleSolid className="h-5 w-5" />
                        Subscribed
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(track.id)}
                        disabled={isSubscribing || subscribing !== null}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
                      >
                        {isSubscribing ? (
                          'Subscribing...'
                        ) : (
                          <>
                            <PlusCircleIcon className="h-5 w-5" />
                            Subscribe
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseContentTracks;




